/**
 * Role-aware landing-path resolution.
 *
 * Single source of truth for:
 *   - which landing-path presets are exposed in the settings dropdown
 *   - which paths each role is permitted to land on after login
 *   - the default landing per role
 *
 * Used by login.component.ts and role-select.component.ts so the post-login
 * navigation respects user.preferences.defaultLandingPath when it is both
 * set AND permitted for the active role; otherwise falls back to the role
 * default (cashier for Admin/Manager/Server, etc).
 */

export interface LandingPathPreset {
    /** Stable identifier persisted in user.preferences.defaultLandingPath */
    path: string;
    /** Human label shown in the settings dropdown */
    label: string;
    /** Roles that may select / land on this path */
    allowedRoles: ReadonlyArray<string>;
}

export const LANDING_PRESETS: ReadonlyArray<LandingPathPreset> = [
    {
        path: '/sidebar/cashier',
        label: 'Cashier (Register)',
        allowedRoles: ['Admin', 'Manager', 'Server']
    },
    {
        path: '/sidebar/admin/waiterMenu/serverOrder',
        label: 'Dining (Tables)',
        allowedRoles: ['Admin', 'Manager']
    },
    {
        path: '/sidebar/admin/waiterMenu/ordersList',
        label: 'Orders (Takeout / Delivery)',
        allowedRoles: ['Admin', 'Manager']
    },
    {
        path: '/sidebar/admin/dashboard',
        label: 'Dashboard',
        allowedRoles: ['Admin']
    },
    {
        path: '/sidebar/admin/inventory',
        label: 'Inventory',
        allowedRoles: ['Admin']
    }
];

/**
 * The path each role goes to when they have no preference saved. Anchors
 * the rest of the app: changing this changes the post-login default UX.
 *
 * Per Bug 2, Manager/Server/Admin all land on cashier by default. Other
 * specialised roles keep their existing post-login destinations.
 */
export const DEFAULT_LANDING_BY_ROLE: { readonly [role: string]: string } = {
    Admin: '/sidebar/cashier',
    Manager: '/sidebar/cashier',
    Server: '/sidebar/cashier',
    Table: '/tableSetup',
    Kiosk: '/kiosk',
    QROrder: '/QROrder',
    Jukebox: '/youtube',
    Rewards: '/rewards',
    PaymentsDisplay: '/paymentUser/paymentUser',
    OrdersDisplay: '/orderUser/orderUser',
    ConfirmationDisplay: '/confirmation-display'
};

export function isPathAllowedForRole(role: string | undefined, path: string | undefined): boolean {
    if (!role || !path) {
        return false;
    }
    const preset = LANDING_PRESETS.find(p => p.path === path);
    if (!preset) {
        return false;
    }
    return preset.allowedRoles.indexOf(role) !== -1;
}

export function presetsForRole(role: string | undefined): LandingPathPreset[] {
    if (!role) {
        return [];
    }
    return LANDING_PRESETS.filter(p => p.allowedRoles.indexOf(role) !== -1);
}

/**
 * Resolve the post-login landing path for a (role, savedPreference) pair.
 *
 * Order of precedence:
 *   1. saved preference, if it is a known preset AND permitted for the role
 *   2. role default from DEFAULT_LANDING_BY_ROLE
 *   3. /login (unknown role — bounce back to login screen)
 *
 * Per the bug ticket: when an admin has a saved preference but later loses
 * permission for it, we silently fall back to the role default rather than
 * stranding them on a permission-denied screen.
 */
export function resolveLandingPath(role: string | undefined, savedPath: string | undefined): string {
    if (savedPath && isPathAllowedForRole(role, savedPath)) {
        return savedPath;
    }
    if (role && DEFAULT_LANDING_BY_ROLE[role]) {
        return DEFAULT_LANDING_BY_ROLE[role];
    }
    return '/login';
}
